import { useMemo } from "react";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { BUSINESS_SETTINGS_ROUTES } from "@/business-settings/constants";
import { useBusiness } from "@/core/business";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { dayjs } from "@/core/utils/date";
import {
  TextMessageListItem,
  TextMessageListItemSkeleton,
} from "@/text-messages/components/text-message-list-item";
import {
  useGetInboundTextMessages,
  usePrefetchCustomerTextMessages,
} from "@/text-messages/query";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";
import { DashboardLink } from "./dashboard-link";

const LOADING_TEXT_MESSAGES = [0, 1, 2, 3];

interface TextMessagesPaneProps {
  selectedItem: IMostRecentTextMessage | null;
  setSelectedItem: (item: IMostRecentTextMessage | null) => void;
  setIsSidebarOpen: (open: boolean) => void;
}

const TextMessagesPane = ({
  selectedItem,
  setSelectedItem,
  setIsSidebarOpen,
}: TextMessagesPaneProps) => {
  const { businessId } = useBusiness();
  const {
    data: messages,
    isLoading,
    isError,
  } = useGetInboundTextMessages(businessId);

  const mostRecentTextMessages = useMemo(
    () =>
      [...(messages || [])]
        .sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)))
        .slice(0, 5),
    [messages],
  );

  const prefetchCustomerTextMessages = usePrefetchCustomerTextMessages();

  const { schedule: schedulePrefetch, cancel: cancelPrefetch } =
    useDelayedAction(prefetchCustomerTextMessages, 250);

  const renderBody = () => {
    if (isLoading) {
      return LOADING_TEXT_MESSAGES.map((index) => (
        <TextMessageListItemSkeleton key={index} index={index} />
      ));
    }
    if (isError)
      return (
        <Text tone="destructive" role="alert" className="py-8">
          Couldn't load text messages. Please try again.
        </Text>
      );
    if (mostRecentTextMessages.length === 0)
      return (
        <Empty
          role={isError ? "alert" : undefined}
          className={emptyStateVariants({ layout: "inline" })}
        >
          <EmptyHeader>
            <EmptyDescription>No text messages yet.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      );
    return mostRecentTextMessages.map((t) => (
      <TextMessageListItem
        key={t.id}
        textMessage={t}
        isSelected={selectedItem?.id === t.id}
        onSelect={() => {
          setSelectedItem(t);
          setIsSidebarOpen(true);
        }}
        onMouseEnter={() => schedulePrefetch(t.customer.id)}
        onMouseLeave={cancelPrefetch}
      />
    ));
  };

  return (
    <Card className="min-w-0 flex-1 gap-0 py-0">
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={2}
        className="w-full p-4"
      >
        <Text variant="title">Recent text messages</Text>
        <DashboardLink
          to={`/business-settings/${businessId}${BUSINESS_SETTINGS_ROUTES.textMessages}`}
          label="View all"
        />
      </Stack>
      <Separator />
      {/* px-1 plus each row's own px-3 lines the text up with the title. */}
      <Stack align="start" gap={0} className="min-h-0 w-full flex-1 p-1">
        {renderBody()}
      </Stack>
    </Card>
  );
};

export default TextMessagesPane;
